/**
 * Outlook Microsoft Graph tools — mailbox- and calendar-WIDE reads that the
 * Office.js item API cannot do (it only sees the one open item).
 *
 * Same shape as the Office.js tools in outlook-tools.ts (`ParentToolSchema` +
 * handler), so the postMessage bridge is unchanged — the only difference is the
 * data source. Each handler acquires a Graph token via `caps.acquireGraphToken`
 * (injected by the shell from the NAA provider) and fetches Microsoft Graph
 * directly:
 *
 *   const token = await caps.acquireGraphToken(["Mail.Read"]);
 *   fetch("https://graph.microsoft.com/v1.0/me/messages", { Authorization: Bearer });
 *
 * Security: the Graph token is used HERE, in the task pane, and never returned to
 * the model or sent across the chat bridge — only the shaped Graph *results* flow
 * back. When the user hasn't connected Microsoft 365, `acquireGraphToken` throws
 * `GraphConsentRequired`, which the shell maps to a structured
 * `{ status: "interaction_required" }` result; this module lets it propagate.
 *
 * Scopes are read-only and least-privilege (Mail.Read, Calendars.Read) — the union
 * is consented once via the "Connect Microsoft 365" button; per-tool acquires are
 * then silent. The manifest `<Permissions>` stays ReadWriteItem (Graph scopes are
 * granted on the app registration, not the manifest).
 */

import { ParentToolSchema, ToolCallCapabilities } from "@usable/office-embed";

const GRAPH_BASE = "https://graph.microsoft.com/v1.0";
const MAIL_READ = ["Mail.Read"];
const CALENDARS_READ = ["Calendars.Read"];

/** Default look-ahead when list_calendar_events is called without an explicit end. */
const DEFAULT_CALENDAR_HORIZON_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/** Graph tool handler — mirrors the Office tools but also receives `caps`. */
export type GraphToolHandler = (
  args: Record<string, unknown>,
  caps?: ToolCallCapabilities
) => Promise<unknown>;

export interface GraphTool {
  schema: ParentToolSchema;
  handler: GraphToolHandler;
}

// ---------------------------------------------------------------------------
// Graph plumbing
// ---------------------------------------------------------------------------

/**
 * Acquire a token for `scopes` and GET a Graph path (already query-built).
 * `acquireGraphToken` may throw `GraphConsentRequired` (not connected) — we let it
 * propagate so the shell maps it to interaction_required. A non-2xx Graph response
 * becomes a clear error the model can relay.
 */
async function graphGet(
  caps: ToolCallCapabilities | undefined,
  scopes: string[],
  pathWithQuery: string
): Promise<Record<string, unknown>> {
  if (!caps?.acquireGraphToken) {
    throw new Error("Microsoft Graph isn't configured for this add-in.");
  }
  const token = await caps.acquireGraphToken(scopes);
  const res = await fetch(`${GRAPH_BASE}${pathWithQuery}`, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(
      `Microsoft Graph request failed (${res.status} ${res.statusText}). ${detail.slice(0, 300)}`
    );
  }
  return (await res.json()) as Record<string, unknown>;
}

/** Build a `?a=b&c=d` query string with literal OData `$` keys, values encoded. */
function query(params: Record<string, string | number | undefined>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === "") continue;
    parts.push(`${k}=${encodeURIComponent(String(v))}`);
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

/** Clamp a caller-supplied count into [1, max] with a default. */
function clampCount(value: unknown, def: number, max: number): number {
  const n = typeof value === "number" ? value : Number.parseInt(String(value ?? ""), 10);
  if (!Number.isFinite(n)) return def;
  return Math.max(1, Math.min(max, Math.trunc(n)));
}

interface GraphEmailAddress {
  name?: string;
  address?: string;
}
function mapAddress(holder: { emailAddress?: GraphEmailAddress } | undefined): {
  name: string;
  address: string;
} | null {
  const ea = holder?.emailAddress;
  if (!ea) return null;
  return { name: ea.name ?? "", address: ea.address ?? "" };
}

function mapMessage(m: Record<string, unknown>): Record<string, unknown> {
  return {
    id: m.id ?? null,
    subject: (m.subject as string) ?? "",
    from: mapAddress(m.from as { emailAddress?: GraphEmailAddress } | undefined),
    receivedDateTime: m.receivedDateTime ?? null,
    preview: (m.bodyPreview as string) ?? "",
    isRead: m.isRead ?? null,
    hasAttachments: m.hasAttachments ?? false,
    webLink: m.webLink ?? null,
  };
}

function mapEvent(e: Record<string, unknown>): Record<string, unknown> {
  const location = e.location as { displayName?: string } | undefined;
  return {
    id: e.id ?? null,
    subject: (e.subject as string) ?? "",
    start: e.start ?? null, // { dateTime, timeZone }
    end: e.end ?? null,
    location: location?.displayName ?? "",
    organizer: mapAddress(e.organizer as { emailAddress?: GraphEmailAddress } | undefined),
    isAllDay: e.isAllDay ?? false,
    onlineMeetingUrl:
      // `||` not `??`: Graph can return an empty-string onlineMeetingUrl for an
      // event that still has a populated onlineMeeting.joinUrl (Teams) — `??` would
      // keep the "" and drop the real link.
      (e.onlineMeetingUrl as string) ||
      ((e.onlineMeeting as { joinUrl?: string } | undefined)?.joinUrl ?? null),
    webLink: e.webLink ?? null,
  };
}

const MESSAGE_SELECT = "subject,from,receivedDateTime,bodyPreview,isRead,hasAttachments,webLink";
const EVENT_SELECT =
  "subject,start,end,location,organizer,isAllDay,onlineMeetingUrl,onlineMeeting,webLink";

// ---------------------------------------------------------------------------
// Tools
// ---------------------------------------------------------------------------

export const graphTools: GraphTool[] = [
  {
    schema: {
      name: "list_messages",
      description:
        "List recent emails from across the mailbox (not just the open message) — newest first. Use this for questions like “summarise my recent emails” or “what came in today”. Returns each message's subject, sender, received time, a short preview, read state and whether it has attachments. Requires the user to have connected Microsoft 365 (they'll be prompted if not). For keyword/sender lookups use search_messages instead.",
      parameters: {
        type: "object",
        properties: {
          count: {
            type: "number",
            description: "How many messages to return (default 10, max 50).",
          },
          unreadOnly: {
            type: "boolean",
            description: "When true, only return unread messages.",
          },
        },
      },
    },
    handler: async (args, caps) => {
      const top = clampCount(args.count, 10, 50);
      const params: Record<string, string | number> = {
        $top: top,
        $select: MESSAGE_SELECT,
        $orderby: "receivedDateTime desc",
      };
      if (args.unreadOnly === true) params.$filter = "isRead eq false";
      const data = await graphGet(caps, MAIL_READ, `/me/messages${query(params)}`);
      const value = (data.value as Record<string, unknown>[]) ?? [];
      return { count: value.length, messages: value.map(mapMessage) };
    },
  },

  {
    schema: {
      name: "search_messages",
      description:
        "Search the mailbox for emails matching a free-text query (matches subject, sender, body and more — the same relevance search as the Outlook search box). Use this for “find the email from Jane about the invoice” style questions. Returns the same fields as list_messages. Requires the user to have connected Microsoft 365.",
      parameters: {
        type: "object",
        properties: {
          query: {
            type: "string",
            description:
              "The search text, e.g. \"invoice from jane\" or \"subject:budget\". Uses Microsoft Search relevance ranking.",
          },
          count: {
            type: "number",
            description: "How many results to return (default 10, max 50).",
          },
        },
        required: ["query"],
      },
    },
    handler: async (args, caps) => {
      const q = String(args.query ?? "").trim();
      if (!q) throw new Error('Provide a non-empty "query" to search for.');
      const top = clampCount(args.count, 10, 50);
      // $search wants a quoted value; it cannot be combined with $orderby (Graph
      // ranks by relevance instead).
      const params = {
        $search: `"${q}"`,
        $top: top,
        $select: MESSAGE_SELECT,
      };
      const data = await graphGet(caps, MAIL_READ, `/me/messages${query(params)}`);
      const value = (data.value as Record<string, unknown>[]) ?? [];
      return { count: value.length, query: q, messages: value.map(mapMessage) };
    },
  },

  {
    schema: {
      name: "list_calendar_events",
      description:
        "List calendar events / appointments across the user's calendar. Use this for “what's on my calendar this week”, “what meetings do I have tomorrow”. Give an ISO 8601 `start` and `end` to list everything in that window (recurring meetings are expanded to their occurrences); omit them to list upcoming events (the next 30 days from now). Returns each event's subject, start/end (with time zone), location, organizer, all-day flag and any online-meeting join URL. Requires the user to have connected Microsoft 365.",
      parameters: {
        type: "object",
        properties: {
          start: {
            type: "string",
            description:
              "Window start as ISO 8601 (e.g. \"2026-07-06T00:00:00\"). Provide together with `end` to list a date range.",
          },
          end: {
            type: "string",
            description: "Window end as ISO 8601 (e.g. \"2026-07-13T00:00:00\").",
          },
          count: {
            type: "number",
            description: "Max events to return (default 25, max 50).",
          },
        },
      },
    },
    handler: async (args, caps) => {
      const top = clampCount(args.count, 25, 50);
      const startArg = typeof args.start === "string" ? args.start.trim() : "";
      const endArg = typeof args.end === "string" ? args.end.trim() : "";

      // Always use calendarView: it expands recurring series into occurrences AND
      // takes an explicit window. A bare /me/events ordered by start ascending
      // returns the OLDEST events in the mailbox, not upcoming ones — so anchor an
      // open bound to "now" (start) and to start + horizon (end). This makes a bare
      // call "upcoming events", and a partial window [start, +horizon] / [now, end].
      const now = new Date();
      const startIso = startArg || now.toISOString();
      const anchorMs = Number.isFinite(Date.parse(startIso)) ? Date.parse(startIso) : now.getTime();
      const endIso = endArg || new Date(anchorMs + DEFAULT_CALENDAR_HORIZON_MS).toISOString();

      const path = `/me/calendarView${query({
        startDateTime: startIso,
        endDateTime: endIso,
        $select: EVENT_SELECT,
        $orderby: "start/dateTime",
        $top: top,
      })}`;
      const data = await graphGet(caps, CALENDARS_READ, path);
      const value = (data.value as Record<string, unknown>[]) ?? [];
      return { count: value.length, events: value.map(mapEvent) };
    },
  },
];

export const graphToolSchemas: ParentToolSchema[] = graphTools.map((t) => t.schema);
